/**
 * Footer Component
 *
 * Global footer with legal links and copyright information.
 * Displays on all pages except full-screen scanner pages.
 *
 * Optionally includes the CallToActionSection with info cards and CTA buttons.
 */

import React from "react";
import { Link } from "react-router-dom";
import CallToActionSection from "./CallToActionSection";

interface FooterProps {
  /** Whether to show a minimal version of the footer */
  minimal?: boolean;
  /** Whether to show the CTA section above the footer */
  showCta?: boolean;
  /** Hide info cards in CTA section */
  hideCtaInfoCards?: boolean;
  /** Hide CTA buttons in CTA section */
  hideCtaButtons?: boolean;
}

const Footer: React.FC<FooterProps> = ({
  minimal = false,
  showCta = false,
  hideCtaInfoCards = false,
  hideCtaButtons = false,
}) => {
  const currentYear = new Date().getFullYear();

  if (minimal) {
    return (
      <footer style={styles.minimalFooter}>
        <div style={styles.minimalContent}>
          <Link to="/profile?guest=true" style={styles.minimalLink}>
            Profile
          </Link>
          <span style={styles.minimalDivider}>|</span>
          <a href="/sell-textbooks" style={styles.minimalLink}>
            Sell
          </a>
          <span style={styles.minimalDivider}>|</span>
          <a href="/why.html" style={styles.minimalLink}>
            Why?
          </a>
          <span style={styles.minimalDivider}>|</span>
          <a href="/privacy.html" style={styles.minimalLink}>
            Privacy
          </a>
          <span style={styles.minimalDivider}>|</span>
          <a href="/terms.html" style={styles.minimalLink}>
            Terms
          </a>
        </div>
      </footer>
    );
  }

  return (
    <>
      {/* CTA Section (optional) */}
      {showCta && (
        <CallToActionSection
          hideInfoCards={hideCtaInfoCards}
          hideCtaButtons={hideCtaButtons}
        />
      )}

      {/* Legal Footer */}
      <footer style={styles.footer}>
        <div style={styles.content}>
          <div style={styles.links}>
            <Link to="/profile?guest=true" style={styles.link}>
              Profile
            </Link>
            <span style={styles.divider}>|</span>
            <a href="/sell-textbooks" style={styles.link}>
              Sell Textbooks
            </a>
            <span style={styles.divider}>|</span>
            <a href="/why.html" style={styles.link}>
              Why SBP?
            </a>
            <span style={styles.divider}>|</span>
            <a href="/privacy.html" style={styles.link}>
              Privacy Policy
            </a>
            <span style={styles.divider}>|</span>
            <a href="/terms.html" style={styles.link}>
              Terms of Service
            </a>
            <span style={styles.divider}>|</span>
            <a href="mailto:support@secretbookprice.com" style={styles.link}>
              Contact
            </a>
          </div>
          <p style={styles.copyright}>
            &copy; {currentYear} Secret Book Price. All rights reserved.
          </p>
          <p style={styles.disclaimer}>
            Prices are provided for informational purposes only. Always verify
            with vendors before selling.
          </p>
        </div>
      </footer>
    </>
  );
};

// =============================================================================
// Styles
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  footer: {
    backgroundColor: "#eee9fa",
    borderTop: "1px solid #d8cff3",
    padding: "24px",
    marginTop: "auto",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    textAlign: "center",
  },
  links: {
    marginBottom: "12px",
  },
  link: {
    color: "#6a49a8",
    textDecoration: "none",
    fontSize: "14px",
    transition: "color 0.2s",
  },
  divider: {
    margin: "0 12px",
    color: "#d1d5db",
  },
  copyright: {
    fontSize: "13px",
    color: "#6b7280",
    margin: "0 0 8px 0",
  },
  disclaimer: {
    fontSize: "12px",
    color: "#9ca3af",
    margin: 0,
  },
  // Minimal footer styles
  minimalFooter: {
    padding: "12px",
    textAlign: "center",
  },
  minimalContent: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },
  minimalLink: {
    color: "#9ca3af",
    textDecoration: "none",
    fontSize: "12px",
  },
  minimalDivider: {
    color: "#d1d5db",
    fontSize: "12px",
  },
};

export default Footer;
